// src/sections/CMS/components/customPage/CustomPageBlockRenderer.tsx  (CONSUMER)
import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import CMSConsumerCarouselBanner from '../banner/CMSConsumerCarouselBanner';
import CMSConsumerGallery from '../gallery/CMSConsumerGallery';
import CMSConsumerFeaturedGallery from '../gallery/CMSConsumerFeaturedGallery';
import ComposedBlockRenderer from './ComposedBlockRenderer';
import FilteredProductsBlockRenderer from './FilteredProductsBlockRenderer';
import { CarouselMediaItem, GalleryMediaItem, ChildTileData } from '../../types/CMSTypes';
import AccordionBlockRenderer from './AccordionBlockRenderer';
import FeatureBadgesRenderer from './FeatureBadgesRenderer';
import CollectionCardsBlockRenderer from './CollectionCardsBlockRenderer';
import ItemGroupTilesBlockRenderer from './ItemGroupTilesBlockRenderer';
import ImageTextFeatureRenderer from './ImageTextFeatureRenderer';
import CMSConsumerNewArrivalsGrid from '../grid/CMSConsumerNewArrivalsGrid';
import { getFileServerBase } from './fileServerBase';

// ─── Media Helper ─────────────────────────────────────────────────────────────

// Env-aware file-server origin (shared helper). Custom-page banner videos
// aren't backend-enriched, so they fall back to these bases; a hardcoded
// staging base made prod videos load from staging (slow). See fileServerBase.ts.
const FILE_SERVER_BASE = `${getFileServerBase()}/media/large/`;
// Videos (incl. HLS .m3u8) are served from /media/video/, not /media/large/.
// Mirrors the backend's file_helpers_service.get_media_file_link path split.
const FILE_SERVER_VIDEO_BASE = `${getFileServerBase()}/media/video/`;

const resolveMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${FILE_SERVER_BASE}${url}`;
};

const resolveVideoUrl = (name: string | null | undefined): string => {
  if (!name) return '';
  if (name.startsWith('http://') || name.startsWith('https://')) return name;
  return `${FILE_SERVER_VIDEO_BASE}${name}`;
};

// ─── Field Helpers ────────────────────────────────────────────────────────────

const getFieldValue = (field: any): any => {
  if (!field) return null;
  return field.value ?? field.default ?? null;
};

const getEnrichedOrRaw = (field: any): any => {
  if (!field) return null;
  return field.value_enriched ?? field.value ?? field.default ?? null;
};

// ─── Block Renderers ──────────────────────────────────────────────────────────

// Map a raw/enriched media list (filenames or {media_name, media_url}) to the
// {media_name, media_url} items the banner expects, resolving URLs client-side.
const toCarouselItems = (mediaFiles: any): CarouselMediaItem[] =>
  Array.isArray(mediaFiles)
    ? mediaFiles.map((item: any) => {
      if (typeof item === 'string') {
        return { media_name: item, media_url: resolveMediaUrl(item) };
      }
      return {
        media_name: item.media_name ?? '',
        media_url: item.media_url ? resolveMediaUrl(item.media_url) : resolveMediaUrl(item.media_name),
      };
    })
    : [];

const renderConsumerCarousel = (fields: Record<string, any>) => {
  // Unified carousel block holds a separate consumer banner (original keys) and
  // trade banner (trade_* keys). On the consumer storefront, prefer the consumer
  // banner when it has any content; otherwise fall back to the trade set so a
  // dual-audience page never shows an empty banner.
  const consumerMedia = getEnrichedOrRaw(fields.media_files);
  const consumerVideoName = getFieldValue(fields.video);
  const hasConsumer =
    (Array.isArray(consumerMedia) && consumerMedia.length > 0) || !!consumerVideoName;
  const useTrade = !hasConsumer;

  const mediaFiles = getEnrichedOrRaw(useTrade ? fields.trade_media_files : fields.media_files);
  const width = getFieldValue(useTrade ? fields.trade_width : fields.width) ?? 2880;
  const height = getFieldValue(useTrade ? fields.trade_height : fields.height) ?? 1050;
  const displayOption = getFieldValue(useTrade ? fields.trade_display_option : fields.display_option) ?? 'carousel';

  // The `video` field stores the (HLS) filename; the backend doesn't enrich it
  // (it's not in component_media_fields), so build the /media/video/ link here.
  const video = useTrade ? getFieldValue(fields.trade_video) : consumerVideoName;
  const videoLink = video
    ? (getFieldValue(useTrade ? fields.trade_video_enriched : fields.video_enriched) || resolveVideoUrl(video))
    : '';

  const carouselItems = toCarouselItems(mediaFiles);

  return (
    <CMSConsumerCarouselBanner
      bannerMediaOption={displayOption}
      bannerMediaCarousel={carouselItems}
      bannerMediaVideo={video ?? null}
      bannerMediaVideoLink={videoLink}
      bannerLinkCategoryId={null}
      bannerWidth={width}
      bannerHeight={height}
      viewMode="live"
    />
  );
};

const renderPageTitle = (fields: Record<string, any>) => {
  const title = getFieldValue(fields.page_title);
  if (!title) return null;
  return (
    <Container maxWidth="lg">
      <Typography variant="h1" sx={{ fontSize: { xs: '28px', md: '46px' }, fontWeight: 400, textAlign: 'left' }}>
        {title}
      </Typography>
    </Container>
  );
};

const renderDescription = (fields: Record<string, any>) => {
  const description = getFieldValue(fields.description) ?? getFieldValue(fields.page_description);
  if (!description) return null;
  return (
    <Container maxWidth="lg">
      <Typography sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px', whiteSpace: 'pre-wrap' }}>
        {description}
      </Typography>
    </Container>
  );
};

const renderGallery = (fields: Record<string, any>) => {
  const galleryMediaRaw = getEnrichedOrRaw(fields.gallery_media);
  if (!Array.isArray(galleryMediaRaw) || galleryMediaRaw.length === 0) return null;

  const galleryMedia: GalleryMediaItem[] = galleryMediaRaw
    .map((item: any) => {
      if (item.file_link) {
        return { filename: item.filename ?? '', file_link: resolveMediaUrl(item.file_link), position: normalizePosition(item.position ?? '') };
      }
      return { filename: item.filename ?? '', file_link: resolveMediaUrl(item.filename), position: normalizePosition(item.position ?? '') };
    })
    .filter((item: GalleryMediaItem) => item.file_link);

  if (galleryMedia.length === 0) return null;
  return <CMSConsumerGallery galleryMedia={galleryMedia} marginTop={0} marginBottom={0} />;
};

const normalizePosition = (position: string): string => position.replace(/_/g, ' ');

const renderSpacer = (fields: Record<string, any>) => {
  const height = getFieldValue(fields.vertical_space) ?? getFieldValue(fields.height) ?? 40;
  return <Box sx={{ height: `${height}px`, width: '100%' }} />;
};

const renderTextBlock = (fields: Record<string, any>) => {
  const content = getFieldValue(fields.content) ?? getFieldValue(fields.text);
  if (!content) return null;
  return (
    <Container maxWidth="lg">
      <Typography sx={{ fontSize: '16px', lineHeight: '24px', whiteSpace: 'pre-wrap' }}>{content}</Typography>
    </Container>
  );
};

const renderAccordion = (fields: Record<string, any>) => {
  const items = getFieldValue(fields.accordion_items);
  if (!Array.isArray(items) || items.length === 0) return null;
  return <AccordionBlockRenderer config={{ accordion_items: items }} />;
};

const renderFeaturedCollection = (fields: Record<string, any>) => {
  // Prefer the consumer/native featured collection; fall back to the trade
  // collection when the consumer side has none, so a dual-audience page isn't blank.
  const consumerCollectionId = fields.collection_id?.value ?? fields.collection_id?.default ?? null;
  const useTrade = !consumerCollectionId;

  const enabled = getFieldValue(useTrade ? fields.trade_enabled : fields.enabled);
  if (enabled === false) return null;

  const collectionId = useTrade
    ? (fields.trade_collection_id?.value ?? fields.trade_collection_id?.default ?? null)
    : consumerCollectionId;
  const enriched = useTrade ? fields.trade_collection_id?.value_enriched : fields.collection_id?.value_enriched;
  const featuredCollection = collectionId && enriched ? enriched[collectionId] : null;
  if (!featuredCollection) return null;
  const maxWidth: string = getFieldValue(useTrade ? fields.trade_max_width : fields.max_width) ?? '';
  return (
    <Box
      sx={{
        width: maxWidth ? '100%' : '85%',
        maxWidth: maxWidth || undefined,
        mx: 'auto',
        '& .MuiContainer-root': {
          maxWidth: '100% !important',
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
        },
      }}
    >
      <CMSConsumerFeaturedGallery
        featuredCollection={featuredCollection}
        hasVideo={false}
        invert={false}
        marginTop={0}
        marginBottom={0}
        viewMode="live"
      />
    </Box>
  );
};

const renderComposedComponent = (fields: Record<string, any>) => {
  const layout = getFieldValue(fields.layout) ?? '1col_full';
  const slots = getFieldValue(fields.slots) ?? [];
  const containerConfig = getFieldValue(fields.containerConfig) ?? {};
  if (!Array.isArray(slots) || slots.length === 0) return null;
  return <ComposedBlockRenderer layout={layout} slots={slots} containerConfig={containerConfig} />;
};

const renderCollectionCards = (
  fields: Record<string, any>,
  groupedCollections: Record<string, any[]> | null,
  pageTitle: string,
  viewMode: 'live' | 'draft',
  parentCategoryId: string | null
) => {
  const enabled: boolean = getFieldValue(fields.enabled) ?? true;
  const displayMode = getFieldValue(fields.display_mode) ?? 'grid_6';
  const maxWidth: string = getFieldValue(fields.max_width) ?? '';
  const hoverAnimationEnabled: boolean = getFieldValue(fields.hover_animation_enabled) ?? true;
  const headingEnabled: boolean = getFieldValue(fields.heading_enabled) ?? true;
  const headingOverride: string = getFieldValue(fields.heading_override) ?? '';
  const defaultSort = getFieldValue(fields.default_sort) ?? 'ALPHABETICAL_AZ';
  if (!enabled || !groupedCollections || Object.keys(groupedCollections).length === 0) return null;
  return (
    <CollectionCardsBlockRenderer
      groupedCollections={groupedCollections}
      displayMode={displayMode}
      enabled={enabled}
      pageTitle={pageTitle}
      viewMode={viewMode}
      maxWidth={maxWidth}
      hoverAnimationEnabled={hoverAnimationEnabled}
      headingEnabled={headingEnabled}
      headingOverride={headingOverride}
      defaultSort={defaultSort}
      parentCategoryId={parentCategoryId}
    />
  );
};

// New Arrival Cards: an ordered carousel of hand-picked collections stored in
// `new_arrival_carousel` ([{ collection_id, position, alternative_title }]). The
// sibling `collection_ids` list is enriched into value_enriched =
// { active: { id: tile }, shadow: { id: tile } } (legacy pages use fixed
// collection_1_id/2/3 fields). Rendered through the cycling
// CMSConsumerNewArrivalsGrid so it matches the storefront New Arrivals carousel.
//
// Per-audience toggle (hub): the Trade set lives in `trade_new_arrival_carousel`,
// the Consumer set in `new_arrival_carousel`. Strict per-side: this CONSUMER
// renderer shows ONLY the consumer array — an empty consumer set means "no new
// arrivals on consumer" (no borrowing the trade set). The consumer field is
// present on both legacy and toggle-era pages, so there's no legacy fallback to
// make here. Tiles resolve from the shared `collection_ids.value_enriched` map
// (collection_ids is the union of both lists).
const renderNewArrivalCards = (
  fields: Record<string, any>,
  viewMode: 'live' | 'draft'
) => {
  const carousel = fields.new_arrival_carousel?.value;
  let tiles: ChildTileData[];

  // An array (even empty) means a carousel-/toggle-era page: use it as the source
  // of truth and never fall through to the legacy fixed slots — so an emptied
  // consumer set renders nothing. Only a truly absent array uses legacy slots.
  if (Array.isArray(carousel)) {
    const enriched = fields.collection_ids?.value_enriched;
    const bucket: Record<string, ChildTileData> =
      (viewMode === 'draft' ? enriched?.shadow : enriched?.active) ?? enriched?.active ?? {};
    tiles = carousel
      .slice()
      .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
      .map((item: any) => bucket?.[item.collection_id] ?? null)
      .filter((tile: any): tile is ChildTileData => !!tile);
  } else {
    tiles = ['collection_1_id', 'collection_2_id', 'collection_3_id']
      .map((key) => {
        const enriched = fields[key]?.value_enriched;
        if (!enriched) return null;
        return (viewMode === 'draft' ? enriched.shadow : enriched.active) ?? enriched.active ?? null;
      })
      .filter((tile): tile is ChildTileData => !!tile);
  }

  // On live, only published collections show (draft shows all, like the homepage).
  tiles = tiles.filter((tile) => viewMode === 'draft' || tile.is_published);

  if (tiles.length === 0) return null;

  return (
    <CMSConsumerNewArrivalsGrid
      newArrivalsData={tiles}
      header="New arrivals"
      hasTitles
      hasButtons
      hasBackgroundText={false}
      spacingValue={4}
      marginTop={0}
      marginBottom={0}
      viewMode={viewMode}
    />
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COMPOSED_BLOCK_INDICATOR = ['slots', 'layout', 'containerConfig'];
const isComposedBlock = (fields: Record<string, any>): boolean =>
  COMPOSED_BLOCK_INDICATOR.some(key => key in fields);

// ─── Types ────────────────────────────────────────────────────────────────────

interface PageBlock {
  id: number;
  position: number;
  component_name: string;
  assigned_name: string;
  component_fields: Record<string, any>;
  component_media_fields: string[];
  component_category_link_fields: string[];
}

interface CustomPageBlockRendererProps {
  pageData: PageBlock[];
  pageTitle?: string;
  groupedCollections?: Record<string, any[]> | null;
  multiFilterFields?: Record<string, any> | null;
  // Page-level filter switches from cms_category.multi_filter_switches.
  multiFilterSwitches?: Record<string, any> | null;
  viewMode?: 'live' | 'draft';
  // First parent-link category id — the default tile source for Item Group Tiles.
  pageParentId?: string | null;
  // This page's OWN category id — the tile source when a block uses tile_source 'child'.
  pageOwnId?: string | null;
  // Per-page tile image overrides (category_id → filename) for Item Group Tiles.
  customChildTiles?: Record<string, string> | null;
  // Symbols resolved for this page (top-level `symbols` on the CMS response).
  // Feature Badges uses them to render the badge strip + modal.
  symbols?: Record<string, any> | null;
}

// ─── Main Renderer ────────────────────────────────────────────────────────────

const CustomPageBlockRenderer: React.FC<CustomPageBlockRendererProps> = ({
                                                                           pageData,
                                                                           pageTitle = '',
                                                                           groupedCollections = null,
                                                                           multiFilterFields = null,
                                                                           multiFilterSwitches = null,
                                                                           viewMode = 'live',
                                                                           pageParentId = null,
                                                                           pageOwnId = null,
                                                                           customChildTiles = null,
                                                                           symbols = null,
                                                                         }) => {
  if (!pageData || pageData.length === 0) return null;

  const sorted = [...pageData].sort((a, b) => a.position - b.position);

  // A page can carry the unified carousel under either component name: the hub
  // keeps the original component_name when it converts a legacy "Trade Carousel"
  // into the unified Consumer/Trade block, so such a page has NO "Consumer
  // Carousel" block and its consumer banner lives on the "Trade Carousel" one.
  const hasConsumerCarouselBlock = sorted.some(b => b.component_name === 'Consumer Carousel');

  return (
    <>
      {sorted.map(block => {
        const fields = block.component_fields;
        let rendered: React.ReactNode = null;

        if (isComposedBlock(fields)) {
          rendered = renderComposedComponent(fields);
        } else {
          switch (block.component_name) {
            case 'Consumer Carousel':
              rendered = renderConsumerCarousel(fields);
              break;
            case 'Trade Carousel':
              // Only skip it when the page also has a Consumer Carousel block (a
              // dual-audience page, where rendering both mounted a second, hidden
              // <video> with extra HLS/cold-cache fetches). When it's the page's only
              // carousel it IS the consumer banner, so it must render.
              rendered = hasConsumerCarouselBlock ? null : renderConsumerCarousel(fields);
              break;
            case 'Page Title':
              rendered = renderPageTitle(fields);
              break;
            case 'Description':
            case 'Page Description':
              rendered = renderDescription(fields);
              break;
            case 'Gallery':
              rendered = renderGallery(fields);
              break;
            case 'Spacer':
              rendered = renderSpacer(fields);
              break;
            case 'Text Block':
            case 'Text':
              rendered = renderTextBlock(fields);
              break;
            case 'Accordion':
              rendered = renderAccordion(fields);
              break;
            case 'Featured Collection':
              rendered = renderFeaturedCollection(fields);
              break;
            case 'Collection Cards':
              rendered = renderCollectionCards(fields, groupedCollections, pageTitle, viewMode, pageOwnId);
              break;
            case 'New Arrival Cards':
              rendered = renderNewArrivalCards(fields, viewMode);
              break;
            case 'Filtered Products':
              rendered = (
                <FilteredProductsBlockRenderer
                  fields={fields}
                  multiFilterFields={multiFilterFields}
                  multiFilterSwitches={multiFilterSwitches}
                  pageTitle={pageTitle}
                />
              );
              break;
            case 'Feature Badges':
              rendered = (
                <FeatureBadgesRenderer
                  symbols={symbols}
                  descriptions={getFieldValue(fields.badge_items) ?? []}
                  learnMoreText={getFieldValue(fields.learn_more_text) ?? ''}
                  maxWidth={getFieldValue(fields.max_width) ?? ''}
                />
              );
              break;
            case 'Item Group Tiles':
              rendered = (
                <ItemGroupTilesBlockRenderer
                  fields={fields}
                  pageParentId={pageParentId}
                  pageOwnId={pageOwnId}
                  viewMode={viewMode}
                  customChildTiles={customChildTiles}
                />
              );
              break;
            case 'Image + Text Feature':
              rendered = (
                <ImageTextFeatureRenderer
                  sectionHeading={getFieldValue(fields.section_heading) ?? ''}
                  maxWidth={getFieldValue(fields.max_width) ?? ''}
                  items={getFieldValue(fields.items) ?? []}
                />
              );
              break;
            default:
              rendered = null;
              break;
          }
        }

        if (!rendered) return null;
        return <Box key={`${block.id}-${block.position}`}>{rendered}</Box>;
      })}
    </>
  );
};

export default CustomPageBlockRenderer;